'use client'

import React from 'react'
import Term from '@/components/term'

/**
 * The "Chart overlays" strip under a chart: one checkbox per overlay, with a
 * count where one is known.
 *
 * One component for every chart on the site, so the collection page and the
 * alkanes page offer the same control in the same place. A checkbox that is
 * disabled says why in its title rather than silently doing nothing.
 */
export interface OverlayToggle {
	key: string
	label: string
	checked: boolean
	onChange: (checked: boolean) => void
	/** Shown after the label in parentheses when set. */
	count?: number
	/**
	 * What the overlay is, in a sentence or two. Rendered as a defined term
	 * (dotted underline, definition on hover) so "Band" explains itself.
	 */
	hint?: React.ReactNode
	disabled?: boolean
	/** Why it is disabled, as the title. */
	reason?: string
	testId?: string
}

export default function ChartOverlayToggles({
	items,
	className
}: {
	items: OverlayToggle[]
	className?: string
}) {
	if (items.length === 0) {
		return null
	}
	return (
		<div
			className={`flex flex-wrap items-center gap-4 border-t border-border/60 px-4 py-2 text-xs ${
				className ?? ''
			}`}
		>
			<span className="font-semibold text-muted-foreground">Chart overlays</span>
			{items.map((item) => (
				<label
					key={item.key}
					title={item.disabled ? item.reason : undefined}
					className={`inline-flex items-center gap-1.5 ${
						item.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
					}`}
				>
					<input
						type="checkbox"
						data-testid={item.testId}
						checked={item.checked}
						disabled={item.disabled}
						onChange={(e) => item.onChange(e.target.checked)}
						className="size-3.5 accent-[var(--primary-500)]"
					/>
					{item.hint ? <Term hint={item.hint}>{item.label}</Term> : item.label}
					{typeof item.count === 'number' && (
						<span className="tabular-nums text-muted-foreground">({item.count})</span>
					)}
				</label>
			))}
		</div>
	)
}
